import { useMemo, useState } from "react";
import { useCustom, useCustomMutation } from "@refinedev/core";
import { Button, Field, Modal, SelectField, TextareaField } from "@fawkes/ui";

// kp-Epic 4 (kennelplanning-migration): the room (KennelGroep) + room-type (RuimteType) management
// dialogs. Additive only — these reuse the shared @fawkes/ui Modal/Field/SelectField/TextareaField
// as-is and read/write the kp-Epic-1/4 contract via Refine `useCustom` / `useCustomMutation` against
// RELATIVE urls (the dataProvider prepends `/api`). All mutations run with successNotification:false /
// errorNotification:false; the caller surfaces an inline notice and refetches overview+capacity+types.
//
// Backend contract (read from the controllers + request/response records):
//   GET    kennelplanning/diersoorten            → [{ id, naam }]                       (select feed)
//   GET    kennelplanning/room-types             → [{ id, code, naam, beschrijving, kleur,
//                                                     volgorde, maxCapaciteit, version }]  (select feed)
//   POST   kennelplanning/rooms                  { naam, diersoortId, ruimteTypeCode, positie,
//                                                  maxCapaciteit, startNummer, aantalKennels }
//                                                → 201 { kennelGroepAsielId, naam }
//   PUT    kennelplanning/rooms/{id}             { naam, positie, ruimteTypeCode, maxCapaciteit,
//                                                  aantalKennels, version }
//                                                → 200 { kennelsAdded, kennelsRemoved,
//                                                        releasedDieren, version }
//   DELETE kennelplanning/rooms/{id}             → 200 { releasedDieren }
//   POST   kennelplanning/room-types             { naam, beschrijving, kleur, maxCapaciteit }
//                                                → 201 RoomTypeDto
//   PUT    kennelplanning/room-types/{id}        { naam, beschrijving, kleur, maxCapaciteit, version }
//                                                → 200 RoomTypeDto
//   DELETE kennelplanning/room-types/{id}        → 204
//
// Error mapping (per the controllers): 409 = stale optimistic-lock (concurrent edit), 400 =
// validation. The dataProvider surfaces the server's `{ message }` (or plain text) on the thrown
// HttpError, so the dialogs branch on `error.statusCode` and show `error.message`.

type Translate = (key: string, options?: { args?: unknown[] }, defaultMessage?: string) => string;

/** Diersoort option — `kennelplanning/diersoorten`. */
interface DiersoortOption {
  id?: number | null;
  naam?: string | null;
}

/** A room-type row — `kennelplanning/room-types` (carries version + beschrijving for the editor). */
interface RoomTypeRef {
  id?: number | null;
  code?: string | null;
  naam?: string | null;
  beschrijving?: string | null;
  kleur?: string | null;
  volgorde?: number | null;
  maxCapaciteit?: number | null;
  version?: number | null;
}

/** The five colour tokens the floorplan understands, each with an emoji swatch for the select. */
const COLOUR_OPTIONS: Array<{ value: string; emoji: string }> = [
  { value: "green", emoji: "🟢" },
  { value: "red", emoji: "🔴" },
  { value: "orange", emoji: "🟠" },
  { value: "blue", emoji: "🔵" },
  { value: "grey", emoji: "⚪" },
];

/** Map an HttpError-ish to an inline notice string (409 conflict / 400 validation / generic). */
function errorNotice(err: unknown, t: Translate): string {
  const e = err as { message?: string; statusCode?: number };
  const msg = e?.message?.trim();
  if (e?.statusCode === 409) {
    return msg || t("kennelplanning.groepDialog.conflict");
  }
  if (e?.statusCode === 400) {
    return msg || t("kennelplanning.groepDialog.invalid");
  }
  return msg || t("kennelplanning.groepDialog.error");
}

// ── KennelGroepFormDialog (create + edit a room) ───────────────────────────────────────────────────

/** What the host hands the room dialog when editing an existing room. */
export interface RoomEditTarget {
  kennelGroepAsielId: number;
  naam?: string | null;
  ruimteTypeCode?: string | null;
  /** Current non-deleted kennel count (== aantalKennels) — from the overview/capacity `totaal`. */
  aantalKennels?: number | null;
  /** Optimistic-lock token + display position — round-tripped when the feed carries them. */
  version?: number | null;
  positie?: number | null;
  maxCapaciteit?: number | null;
}

interface RoomForm {
  naam: string;
  diersoortId: string;
  ruimteTypeCode: string;
  positie: string;
  maxCapaciteit: string;
  startNummer: string;
  aantalKennels: string;
}

function emptyRoomForm(target: RoomEditTarget | null): RoomForm {
  return {
    naam: target?.naam ?? "",
    diersoortId: "",
    ruimteTypeCode: target?.ruimteTypeCode ?? "",
    positie: target?.positie != null ? String(target.positie) : "",
    maxCapaciteit: target?.maxCapaciteit != null ? String(target.maxCapaciteit) : "",
    startNummer: "1",
    aantalKennels: target?.aantalKennels != null ? String(target.aantalKennels) : "",
  };
}

export function KennelGroepFormDialog({
  target,
  t,
  onClose,
  onSaved,
}: {
  /** null → create; an existing room → edit (diersoort + startNummer read-only). */
  target: RoomEditTarget | null;
  t: Translate;
  onClose: () => void;
  /** Called after a successful create/update/delete with the inline message to surface. */
  onSaved: (message: string) => void;
}) {
  const isEdit = target != null;
  const [form, setForm] = useState<RoomForm>(() => emptyRoomForm(target));
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmShrink, setConfirmShrink] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data: diersoortenData } = useCustom<DiersoortOption[]>({
    url: "kennelplanning/diersoorten",
    method: "get",
    queryOptions: { retry: false },
  });
  const { data: roomTypesData } = useCustom<RoomTypeRef[]>({
    url: "kennelplanning/room-types",
    method: "get",
    queryOptions: { retry: false },
  });

  const { mutate, isLoading: saving } = useCustomMutation();

  const diersoorten = diersoortenData?.data ?? [];
  const roomTypes = useMemo(
    () => (roomTypesData?.data ?? []).slice().sort((a, b) => (a.volgorde ?? 0) - (b.volgorde ?? 0)),
    [roomTypesData],
  );

  const diersoortOptions = [
    { value: "", label: t("kennelplanning.groepDialog.diersoortPlaceholder") },
    ...diersoorten
      .filter((d) => d.id != null)
      .map((d) => ({ value: String(d.id), label: d.naam ?? String(d.id) })),
  ];
  const ruimteTypeOptions = [
    { value: "", label: t("kennelplanning.groepDialog.ruimteTypePlaceholder") },
    ...roomTypes
      .filter((rt) => rt.code)
      .map((rt) => ({ value: rt.code as string, label: rt.naam ?? (rt.code as string) })),
  ];

  const set = (k: keyof RoomForm, v: string) => {
    setForm((f) => ({ ...f, [k]: v }));
    setNotice(null);
  };

  // Selecting a room type prefills maxCapaciteit from that type (only when empty/unset by the user).
  const onRuimteTypeChange = (code: string) => {
    setNotice(null);
    setForm((f) => {
      const rt = roomTypes.find((r) => r.code === code);
      const prefill =
        rt && rt.maxCapaciteit != null && (f.maxCapaciteit === "" || f.ruimteTypeCode !== code)
          ? String(rt.maxCapaciteit)
          : f.maxCapaciteit;
      return { ...f, ruimteTypeCode: code, maxCapaciteit: prefill };
    });
  };

  const parseIntOrNull = (s: string): number | null => {
    const trimmed = s.trim();
    if (trimmed === "") return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? Math.trunc(n) : null;
  };

  const validate = (): string | null => {
    if (!form.naam.trim()) return t("kennelplanning.groepDialog.naamRequired");
    if (!isEdit && parseIntOrNull(form.diersoortId) == null)
      return t("kennelplanning.groepDialog.diersoortRequired");
    if (parseIntOrNull(form.positie) == null) return t("kennelplanning.groepDialog.positieRequired");
    if (!isEdit && parseIntOrNull(form.startNummer) == null)
      return t("kennelplanning.groepDialog.startNummerRequired");
    const aantal = parseIntOrNull(form.aantalKennels);
    if (aantal == null || aantal < 0) return t("kennelplanning.groepDialog.aantalRequired");
    return null;
  };

  const doSave = () => {
    setNotice(null);
    const ruimteTypeCode = form.ruimteTypeCode.trim() || null;
    const maxCapaciteit = parseIntOrNull(form.maxCapaciteit);
    const aantalKennels = parseIntOrNull(form.aantalKennels);

    if (isEdit && target) {
      mutate(
        {
          url: `kennelplanning/rooms/${target.kennelGroepAsielId}`,
          method: "put",
          values: {
            naam: form.naam.trim(),
            positie: parseIntOrNull(form.positie),
            ruimteTypeCode,
            maxCapaciteit,
            aantalKennels,
            version: target.version ?? null,
          },
          successNotification: false,
          errorNotification: false,
        },
        {
          onSuccess: (res) => {
            const r = res?.data as
              | { kennelsAdded?: number; kennelsRemoved?: number; releasedDieren?: number }
              | undefined;
            const added = r?.kennelsAdded ?? 0;
            const removed = r?.kennelsRemoved ?? 0;
            const released = r?.releasedDieren ?? 0;
            onSaved(
              t("kennelplanning.groepDialog.resizeResult", { args: [added, removed, released] }),
            );
          },
          onError: (err) => setNotice(errorNotice(err, t)),
        },
      );
    } else {
      mutate(
        {
          url: "kennelplanning/rooms",
          method: "post",
          values: {
            naam: form.naam.trim(),
            diersoortId: parseIntOrNull(form.diersoortId),
            ruimteTypeCode,
            positie: parseIntOrNull(form.positie),
            maxCapaciteit,
            startNummer: parseIntOrNull(form.startNummer),
            aantalKennels,
          },
          successNotification: false,
          errorNotification: false,
        },
        {
          onSuccess: () => onSaved(t("kennelplanning.groepDialog.created")),
          onError: (err) => setNotice(errorNotice(err, t)),
        },
      );
    }
  };

  const onSaveClick = () => {
    const v = validate();
    if (v) {
      setNotice(v);
      return;
    }
    // Shrink confirm: edit-only, when the new kennel count is below the current count.
    if (isEdit && target?.aantalKennels != null) {
      const next = parseIntOrNull(form.aantalKennels);
      if (next != null && next < target.aantalKennels && !confirmShrink) {
        setConfirmShrink(true);
        return;
      }
    }
    setConfirmShrink(false);
    doSave();
  };

  const doDelete = () => {
    if (!target) return;
    setNotice(null);
    mutate(
      {
        url: `kennelplanning/rooms/${target.kennelGroepAsielId}`,
        method: "delete",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => {
          const released = (res?.data as { releasedDieren?: number } | undefined)?.releasedDieren ?? 0;
          onSaved(t("kennelplanning.groepDialog.deleted", { args: [released] }));
        },
        onError: (err) => setNotice(errorNotice(err, t)),
      },
    );
  };

  const title = isEdit
    ? t("kennelplanning.groepDialog.editTitle")
    : t("kennelplanning.groepDialog.createTitle");

  return (
    <Modal
      open
      title={title}
      onClose={onClose}
      footer={
        <>
          {notice && (
            <span className="fawkes-error fawkes-kp-dialog__notice" role="status">
              {notice}
            </span>
          )}
          {isEdit && (
            <Button variant="secondary" onClick={() => setConfirmDelete(true)} disabled={saving}>
              {t("common.delete")}
            </Button>
          )}
          <div className="fawkes-kp-dialog__footer-spacer" />
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t("common.cancel")}
          </Button>
          <Button variant="primary" onClick={onSaveClick} disabled={saving}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <div className="fawkes-kp-dialog">
        <div className="fawkes-form-grid">
          <Field
            label={t("kennelplanning.groepDialog.naam")}
            value={form.naam}
            onChange={(e) => set("naam", e.target.value)}
            required
          />
          {isEdit ? (
            // Diersoort is fixed after creation and not carried by the read feed → read-only.
            <Field
              label={t("kennelplanning.groepDialog.diersoort")}
              value={t("kennelplanning.groepDialog.diersoortLocked")}
              disabled
              readOnly
            />
          ) : (
            <SelectField
              label={t("kennelplanning.groepDialog.diersoort")}
              options={diersoortOptions}
              value={form.diersoortId}
              onChange={(e) => set("diersoortId", e.target.value)}
              required
            />
          )}
          <SelectField
            label={t("kennelplanning.groepDialog.ruimteType")}
            options={ruimteTypeOptions}
            value={form.ruimteTypeCode}
            onChange={(e) => onRuimteTypeChange(e.target.value)}
          />
          <Field
            label={t("kennelplanning.groepDialog.positie")}
            type="number"
            value={form.positie}
            onChange={(e) => set("positie", e.target.value)}
          />
          <Field
            label={t("kennelplanning.groepDialog.maxCapaciteit")}
            type="number"
            min={0}
            value={form.maxCapaciteit}
            onChange={(e) => set("maxCapaciteit", e.target.value)}
          />
          {isEdit ? (
            // Start number only applies at creation (kennels keep their numbers) → read-only.
            <Field
              label={t("kennelplanning.groepDialog.startNummer")}
              value={t("kennelplanning.groepDialog.startNummerLocked")}
              disabled
              readOnly
            />
          ) : (
            <Field
              label={t("kennelplanning.groepDialog.startNummer")}
              type="number"
              value={form.startNummer}
              onChange={(e) => set("startNummer", e.target.value)}
            />
          )}
          <Field
            label={t("kennelplanning.groepDialog.aantalKennels")}
            type="number"
            min={0}
            value={form.aantalKennels}
            onChange={(e) => set("aantalKennels", e.target.value)}
          />
        </div>

        {confirmShrink && (
          <div className="fawkes-kp-dialog__confirm" role="alert">
            <span>{t("kennelplanning.groepDialog.shrinkConfirm")}</span>
            <div className="fawkes-kp-dialog__confirm-actions">
              <Button variant="secondary" onClick={() => setConfirmShrink(false)} disabled={saving}>
                {t("common.cancel")}
              </Button>
              <Button variant="primary" onClick={doSave} disabled={saving}>
                {t("kennelplanning.groepDialog.shrinkConfirmAccept")}
              </Button>
            </div>
          </div>
        )}

        {confirmDelete && (
          <div className="fawkes-kp-dialog__confirm fawkes-kp-dialog__confirm--danger" role="alert">
            <span>{t("kennelplanning.groepDialog.deleteConfirm")}</span>
            <div className="fawkes-kp-dialog__confirm-actions">
              <Button variant="secondary" onClick={() => setConfirmDelete(false)} disabled={saving}>
                {t("common.cancel")}
              </Button>
              <Button variant="primary" onClick={doDelete} disabled={saving}>
                {t("common.delete")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── RuimteTypeFormDialog (create + edit a room-type) ───────────────────────────────────────────────

/** What the host hands the room-type dialog when editing (volgorde is server-assigned, not shown). */
export interface RoomTypeEditTarget {
  id: number;
  naam?: string | null;
  beschrijving?: string | null;
  kleur?: string | null;
  maxCapaciteit?: number | null;
  version?: number | null;
}

interface RoomTypeForm {
  naam: string;
  beschrijving: string;
  kleur: string;
  maxCapaciteit: string;
}

function emptyRoomTypeForm(target: RoomTypeEditTarget | null): RoomTypeForm {
  return {
    naam: target?.naam ?? "",
    beschrijving: target?.beschrijving ?? "",
    kleur: target?.kleur?.toLowerCase() ?? "green",
    maxCapaciteit: target?.maxCapaciteit != null ? String(target.maxCapaciteit) : "",
  };
}

export function RuimteTypeFormDialog({
  target,
  t,
  onClose,
  onSaved,
}: {
  /** null → create; an existing room-type → edit. */
  target: RoomTypeEditTarget | null;
  t: Translate;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const isEdit = target != null;
  const [form, setForm] = useState<RoomTypeForm>(() => emptyRoomTypeForm(target));
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { mutate, isLoading: saving } = useCustomMutation();

  const set = (k: keyof RoomTypeForm, v: string) => {
    setForm((f) => ({ ...f, [k]: v }));
    setNotice(null);
  };

  const colourOptions = COLOUR_OPTIONS.map((c) => ({
    value: c.value,
    label: `${c.emoji} ${t(`kennelplanning.ruimteTypeDialog.kleur.${c.value}`)}`,
  }));

  const parseIntOrNull = (s: string): number | null => {
    const trimmed = s.trim();
    if (trimmed === "") return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? Math.trunc(n) : null;
  };

  const doSave = () => {
    setNotice(null);
    if (!form.naam.trim()) {
      setNotice(t("kennelplanning.ruimteTypeDialog.naamRequired"));
      return;
    }
    const values = {
      naam: form.naam.trim(),
      beschrijving: form.beschrijving.trim() || null,
      kleur: form.kleur,
      maxCapaciteit: parseIntOrNull(form.maxCapaciteit),
    };
    if (isEdit && target) {
      mutate(
        {
          url: `kennelplanning/room-types/${target.id}`,
          method: "put",
          values: { ...values, version: target.version ?? null },
          successNotification: false,
          errorNotification: false,
        },
        {
          onSuccess: () => onSaved(t("kennelplanning.ruimteTypeDialog.updated")),
          onError: (err) => setNotice(errorNotice(err, t)),
        },
      );
    } else {
      mutate(
        {
          url: "kennelplanning/room-types",
          method: "post",
          values,
          successNotification: false,
          errorNotification: false,
        },
        {
          onSuccess: () => onSaved(t("kennelplanning.ruimteTypeDialog.created")),
          onError: (err) => setNotice(errorNotice(err, t)),
        },
      );
    }
  };

  const doDelete = () => {
    if (!target) return;
    setNotice(null);
    mutate(
      {
        url: `kennelplanning/room-types/${target.id}`,
        method: "delete",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => onSaved(t("kennelplanning.ruimteTypeDialog.deleted")),
        onError: (err) => setNotice(errorNotice(err, t)),
      },
    );
  };

  const title = isEdit
    ? t("kennelplanning.ruimteTypeDialog.editTitle")
    : t("kennelplanning.ruimteTypeDialog.createTitle");

  return (
    <Modal
      open
      title={title}
      onClose={onClose}
      footer={
        <>
          {notice && (
            <span className="fawkes-error fawkes-kp-dialog__notice" role="status">
              {notice}
            </span>
          )}
          {isEdit && (
            <Button variant="secondary" onClick={() => setConfirmDelete(true)} disabled={saving}>
              {t("common.delete")}
            </Button>
          )}
          <div className="fawkes-kp-dialog__footer-spacer" />
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t("common.cancel")}
          </Button>
          <Button variant="primary" onClick={doSave} disabled={saving}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <div className="fawkes-kp-dialog">
        <div className="fawkes-form-grid">
          <Field
            label={t("kennelplanning.ruimteTypeDialog.naam")}
            value={form.naam}
            onChange={(e) => set("naam", e.target.value)}
            required
          />
          <SelectField
            label={t("kennelplanning.ruimteTypeDialog.kleurLabel")}
            options={colourOptions}
            value={form.kleur}
            onChange={(e) => set("kleur", e.target.value)}
          />
          <Field
            label={t("kennelplanning.ruimteTypeDialog.maxCapaciteit")}
            type="number"
            min={0}
            value={form.maxCapaciteit}
            onChange={(e) => set("maxCapaciteit", e.target.value)}
          />
          <TextareaField
            label={t("kennelplanning.ruimteTypeDialog.beschrijving")}
            value={form.beschrijving}
            onChange={(e) => set("beschrijving", e.target.value)}
            rows={3}
          />
        </div>

        {confirmDelete && (
          <div className="fawkes-kp-dialog__confirm fawkes-kp-dialog__confirm--danger" role="alert">
            <span>{t("kennelplanning.ruimteTypeDialog.deleteConfirm")}</span>
            <div className="fawkes-kp-dialog__confirm-actions">
              <Button variant="secondary" onClick={() => setConfirmDelete(false)} disabled={saving}>
                {t("common.cancel")}
              </Button>
              <Button variant="primary" onClick={doDelete} disabled={saving}>
                {t("common.delete")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
